import type { MouseEvent } from "react";
import { useCallback } from "react";

import {
    FONT_SIZE_CHANGE_EVENT,
    FONT_SIZE_STORAGE_KEY,
    FONT_SIZE_VALUES,
    parseSize,
} from "../constants";
import { usePersistedSetting } from "./usePersistedSetting";

/**
 * Manages the user's preferred code-block text size.
 *
 * The selected size is persisted in localStorage and kept in sync across all
 * mounted consumers via the {@link FONT_SIZE_CHANGE_EVENT} window event. The size
 * is constrained to the discrete steps defined by {@link FONT_SIZE_VALUES}; invalid
 * stored values fall back to `100`.
 *
 * @returns An object containing:
 *   - `size` - The current size as a number (e.g. `100`).
 *   - `handleSizeCommitted` - A slider `onChangeCommitted` handler that
 *     accepts a single numeric value and persists it.
 *   - `handleDecrease` - A click handler that steps the size down by one
 *     discrete step, clamped at the minimum.
 *   - `handleIncrease` - A click handler that steps the size up by one
 *     discrete step, clamped at the maximum.
 */
export function useCodeTextSize() {
    const FONT_SIZE_DEFAULT = 100;
    const [rawSize, setRawSize] = usePersistedSetting(
        FONT_SIZE_STORAGE_KEY,
        String(FONT_SIZE_DEFAULT),
        FONT_SIZE_CHANGE_EVENT
    );

    const size = parseSize(rawSize);
    const sizeIndex = FONT_SIZE_VALUES.indexOf(size);

    const commitSize = useCallback(
        (newValue: number) => setRawSize(String(newValue)),
        [setRawSize]
    );

    const handleSizeCommitted = useCallback(
        (_event: unknown, newValue: number | number[]) => {
            if (!Array.isArray(newValue)) {
                commitSize(newValue);
            }
        },
        [commitSize]
    );

    const handleDecrease = useCallback(
        (_event: MouseEvent) => {
            if (sizeIndex <= 0) return;
            commitSize(FONT_SIZE_VALUES[sizeIndex - 1]);
        },
        [sizeIndex, commitSize]
    );

    const handleIncrease = useCallback(
        (_event: MouseEvent) => {
            if (sizeIndex >= FONT_SIZE_VALUES.length - 1) return;
            commitSize(FONT_SIZE_VALUES[sizeIndex + 1]);
        },
        [sizeIndex, commitSize]
    );

    return {
        size,
        handleSizeCommitted,
        handleDecrease,
        handleIncrease,
    } as const;
}
